import React from "react";
import type { ApolloClient } from "@apollo/client";
import moment from "moment";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import { BsTrash } from "react-icons/bs";
import { toast } from "react-toastify";
import Alert from "components/Alert";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import {
  hasEnabledPayrollIntegration,
  hasFullyOnboarded,
  hasStatePlanSelector,
} from "store/selectors/employer";
import { employerConstants } from "actions/types";
import {
  debounce,
  every,
  filter,
  get,
  isEmpty,
  isEqual,
  keyBy,
  keys,
  map,
  pickBy,
  size,
  some,
  transform,
} from "lodash";
import { FiAlertTriangle, FiPlus, FiUpload, FiUser } from "react-icons/fi";
import {
  Card,
  Col,
  Form,
  OverlayTrigger,
  Popover,
  Row,
  Table,
} from "react-bootstrap";

import {
  getAllEmployerGroups,
  getPaginatedInvites,
  resendInvitation,
  resendInvitations,
  revokeInvitation,
  updateProgressiveOnboarding,
} from "actions/employerActions";
import {
  inviteStatusToEnglish,
  inviteStatusToType,
} from "statics/statusToEnglishMapping";

import IconSpinner from "components/IconSpinner";
import StatusBox from "components/StatusBox";
import Paginator from "components/Paginator";
import Button from "components/Button";
import AddEmployeeForm from "./AddEmployeeForm";
import IconMessageCard from "components/IconMessageCard";

import "./EmployeeInvitationsTable.scss";
import { progressiveOnboardingEvents } from "statics/states";
import type { RootState } from "types/state";

const allGroups = {
  id: "ALL_GROUPS",
  value: "ALL_GROUPS",
  name: "All Groups",
};

/** Group as rendered/consumed by this table (loose employer slice). */
interface EmployerGroup {
  id: string;
  value?: string;
  name?: string;
  useUniqueInviteCodes?: boolean;
}

/** Employee invitation row as returned by getPaginatedInvites. */
interface EmployeeInvitation {
  id: string;
  status: string;
  groupId?: string;
  userEmail?: string;
  expiresAt?: string;
  createdAt?: string;
}

const errorSelector = createErrorSelector([
  employerConstants.GET_EMPLOYEE_INVITES,
  employerConstants.RESEND_INVITE_EMPLOYEE,
  employerConstants.REVOKE_INVITE_EMPLOYEE,
  employerConstants.RESEND_INVITES_EMPLOYEE,
  employerConstants.GET_PAGINATED_INVITES,
]);

const isLoadingSelector = createLoadingSelector(
  employerConstants.GET_PAGINATED_INVITES
);

const isResendingSelector = createLoadingSelector([
  employerConstants.RESEND_INVITE_EMPLOYEE,
]);

const isResendingBulkSelector = createLoadingSelector([
  employerConstants.RESEND_INVITES_EMPLOYEE,
]);

const isRevokingSelector = createLoadingSelector(
  employerConstants.REVOKE_INVITE_EMPLOYEE
);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isResending: isResendingSelector(state),
    isResendingBulk: isResendingBulkSelector(state),
    // employer slice is loosely typed (Record<string, unknown>) — cast at boundary
    groupsById: keyBy(state.employer.groups, "id") as unknown as Record<
      string,
      EmployerGroup
    >,
    paginatedEmployeeInvitations: state.employer
      .paginatedEmployeeInvitations as EmployeeInvitation[],
    paginatedEmployeeInvitationsCount:
      state.employer.paginatedEmployeeInvitationsCount,
    groups: state.employer.groups as unknown as EmployerGroup[],
    hasStatePlan: hasStatePlanSelector(state),
    isFetching: isLoadingSelector(state),
    isRevoking: isRevokingSelector(state),
    hasFullyOnboarded: hasFullyOnboarded(state),
    hasPayrollIntegration: hasEnabledPayrollIntegration(state),
  };
};

const mapDispatchToProps = {
  push,
  resendInvitation,
  revokeInvitation,
  resendInvitations,
  getPaginatedInvites,
  getAllEmployerGroups,
  updateProgressiveOnboarding,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  showConfirmationModal: boolean;
  pendingCancellationId: string | null;
  selectedInvitations: Record<string, boolean>;
  selectAllInvitations: Record<string, boolean>;
  limit: number;
  offset: number;
  search: string;
  page: number;
  selectedGroup: string;
  showForm: boolean;
  selectEveryInvitation: boolean;
  /** set in componentDidMount; read indirectly only */
  initialFetching?: boolean;
  /** set in _handleChange, read in _getPaginatedInvites */
  groupId?: string;
}

const clearedSelection = (): Pick<
  State,
  "selectedInvitations" | "selectAllInvitations" | "selectEveryInvitation"
> => ({
  selectedInvitations: {},
  selectAllInvitations: {},
  selectEveryInvitation: false,
});

export class EmployeeInvitationsTable extends React.PureComponent<
  Props,
  State
> {
  _textInput!: HTMLInputElement | null;

  constructor(props: Props) {
    super(props);

    this.state = {
      showConfirmationModal: false,
      pendingCancellationId: null,
      selectedInvitations: {},
      selectAllInvitations: {},
      limit: 10,
      offset: 0,
      search: "",
      page: 0,
      selectedGroup: "ALL_GROUPS",
      showForm: false,
      selectEveryInvitation: false,
    };
  }

  componentDidMount() {
    void this._getPaginatedInvites().then(() => {
      this.setState({ initialFetching: false });
    });
  }

  _onPageChange = ({ selected }: { selected: number }) => {
    const offset = selected * this.state.limit;

    this.setState({ page: selected, offset }, () =>
      this._getPaginatedInvites()
    );
  };

  _getPaginatedInvites = () => {
    return this.props.getPaginatedInvites(
      this.props.client as ApolloClient<object>,
      {
        limit: this.state.limit,
        offset: this.state.offset,
        search: this.state.search,
        groupId: this.state.groupId,
      }
    );
  };

  _onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState(
      {
        search: e.target.value,
        offset: 0,
        page: 0,
        ...clearedSelection(),
      },
      () => this._getPaginatedInvites()
    );
  };

  _debounceOnChange = debounce(this._onSearchChange, 500);

  _resendInvitation = (invitation: EmployeeInvitation) => {
    void this.props
      .resendInvitation(
        this.props.client as ApolloClient<object>,
        // EmployeeInvitation is looser than the action's required shape; the
        // loose employer slice means fields are optional here.
        invitation as Parameters<Props["resendInvitation"]>[1]
      )
      .then((result) => {
        if (result.success) {
          toast.success("Successfully resent invitation.");
        }
      });
  };

  _revokeInvitation = (invitationId: string) => {
    void this.props
      .revokeInvitation(this.props.client as ApolloClient<object>, invitationId)
      .then((result) => {
        // close popover
        document.body.click();
        if (result.success) {
          toast.success("Successfully removed invitation.");
        }

        return this._getPaginatedInvites();
      });
  };

  _handleStateFilterChange = () => {
    this.setState(
      {
        offset: 0,
        page: 0,
      },
      () => this._getPaginatedInvites()
    );
  };

  _clearFilters = () => {
    this._textInput!.value = "";
    this.setState(
      {
        search: "",
        offset: 0,
      },
      () => this._getPaginatedInvites()
    );
  };

  _createGroupFilter = () => {
    const groups = this.props.groups.map((group) => {
      return { id: group.id, value: group.id, name: group.name };
    });

    return [allGroups, ...groups];
  };

  _emptyState = () => {
    const showEmptySearch = this.state.search;

    if (showEmptySearch) {
      return (
        <>
          <IconMessageCard
            header={
              "There were no employees found matching your search or filters."
            }
            subheader="Would you like to clear filters?"
            icon={<FiAlertTriangle color="white" stroke="#60A4BF" size={16} />}
            actionText={"Clear Filters"}
            onClick={this._clearFilters}
          />
        </>
      );
    } else if (this.props.hasPayrollIntegration) {
      return (
        <IconMessageCard
          header={"Waiting on roster to sync."}
          icon={<FiUser color="white" stroke="#60A4BF" size={16} />}
        />
      );
    } else {
      return (
        <IconMessageCard
          header={"Get started by inviting an employee!"}
          icon={<FiUser color="white" stroke="#60A4BF" size={16} />}
        />
      );
    }
  };

  buildRows = (showUniqueColumn: boolean) => {
    return this.props.paginatedEmployeeInvitations.map((invite) => {
      let status = invite.status;

      const inviteAccepted = status === "ACCEPTED";
      const invitedRevoked = status === "REVOKED";

      const commonDateFormat = "MMM Do YYYY";
      const group = get(
        this.props.groupsById,
        invite.groupId as string,
        {}
      ) as EmployerGroup;

      const groupHasUniqueCodes = group.useUniqueInviteCodes;

      const everyInviteActive = every(
        // PRE-EXISTING QUIRK: a boolean is passed to lodash `every`, which
        // treats a non-collection as empty and returns `true` vacuously.
        // Preserved as-is.
        this.state.selectEveryInvitation as unknown as boolean[],
        (everyInvite: boolean) => everyInvite
      );

      const hasDeselectedInvite = every(
        this.state.selectedInvitations,
        (selected) => selected
      );

      const selectedAllInvitations = every(
        this.state.selectAllInvitations,
        (checked) => checked
      );

      const clearAll =
        this.state.selectEveryInvitation &&
        everyInviteActive &&
        selectedAllInvitations &&
        hasDeselectedInvite;

      let expirationDate: moment.Moment | undefined;

      if (!inviteAccepted && groupHasUniqueCodes) {
        const invitationExpiresAt = invite.expiresAt;

        const expiresAt = moment(invitationExpiresAt);
        expirationDate = expiresAt;
        if (!invitedRevoked && expiresAt.diff(moment().utc(), "days") < 0) {
          status = "EXPIRED";
        }
      }

      return (
        <tr key={invite.id}>
          <td>
            {!inviteAccepted &&
              !this.props.hasPayrollIntegration &&
              this.props.hasFullyOnboarded && (
                <Form.Check
                  id={invite.id}
                  type="checkbox"
                  name="selectUser"
                  checked={
                    this.state.selectedInvitations[invite.id] ||
                    this.state.selectEveryInvitation
                  }
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (clearAll) {
                      return this.setState(clearedSelection());
                    } else {
                      this.setState({
                        selectedInvitations: {
                          ...this.state.selectedInvitations,
                          [invite.id]: event.target.checked,
                        },
                        selectEveryInvitation: false,
                      });
                    }
                  }}
                />
              )}
          </td>
          <td>{group.name}</td>
          <td>{invite.userEmail}</td>
          <td>{moment(invite.createdAt).format(commonDateFormat)}</td>
          {showUniqueColumn && (
            <td>
              {!inviteAccepted && groupHasUniqueCodes
                ? expirationDate!.format(commonDateFormat)
                : null}
            </td>
          )}
          <td>
            <StatusBox
              status={
                inviteStatusToEnglish[
                  status as keyof typeof inviteStatusToEnglish
                ]
              }
              type={
                inviteStatusToType[status as keyof typeof inviteStatusToType]
              }
            />
          </td>

          <td>
            {!this.props.hasPayrollIntegration && (
              <OverlayTrigger
                rootClose
                trigger="click"
                placement="bottom"
                overlay={
                  <Popover className="action-popover">
                    <Popover.Body>
                      <p>Are you sure you want to remove this invitation?</p>
                      <div>
                        <Button
                          type="button"
                          color="cancel"
                          name="cancel"
                          btnLabel="Cancel"
                          onClick={() => document.body.click()}
                          size="sm"
                        />
                        <Button
                          type="button"
                          color="red"
                          name="submit"
                          btnLabel="Remove"
                          withArrow={true}
                          loading={this.props.isRevoking}
                          onClick={() => this._revokeInvitation(invite.id)}
                          size="sm"
                        />
                      </div>
                    </Popover.Body>
                  </Popover>
                }
              >
                <span className="cancel-action">
                  <BsTrash size={18} color="#B12121" />
                </span>
              </OverlayTrigger>
            )}
          </td>
        </tr>
      );
    });
  };

  _handleOnClick = () => {
    let invitations: string[] | undefined, groupIds: string[] | undefined;
    // A group-scoped payload cannot express the search filter, so while a search
    // is active the resend is narrowed to the ids that are actually checked.
    const useGroupScope =
      this.state.selectEveryInvitation && !this.state.search;

    if (useGroupScope) {
      groupIds =
        this.state.selectedGroup === "ALL_GROUPS"
          ? map(this.props.groups, "id")
          : [this.state.selectedGroup];
    } else {
      invitations = keys(
        pickBy(this.state.selectedInvitations, (value) => value)
      );
    }

    void this.props
      .resendInvitations(this.props.client as ApolloClient<object>, {
        invitations,
        groupIds,
      })
      .then(async (result) => {
        if (!result.success) {
          return;
        }
        if (!this.props.hasFullyOnboarded) {
          // Preserves pre-existing call: updateProgressiveOnboarding expects a
          // third `companyId` arg that this site never passed (companyId stays
          // undefined at runtime).
          // @ts-expect-error pre-existing missing companyId arg, behavior preserved
          void this.props.updateProgressiveOnboarding(
            this.props.client as ApolloClient<object>,
            progressiveOnboardingEvents.LaunchPlan
          );
        }
        await this._getPaginatedInvites();
        toast.success("Successfully sent invitations.");
        this.setState(clearedSelection());
      });
  };

  _clearChecked = () => {
    const everyInviteActive = every(
      // PRE-EXISTING QUIRK: a boolean is passed to lodash `every`, which treats
      // a non-collection as empty and returns `true` vacuously. Preserved as-is.
      this.state.selectEveryInvitation as unknown as boolean[],
      (everyInvite: boolean) => everyInvite
    );
    const invitations = this.props.paginatedEmployeeInvitations;
    const selectedInvites = this.state.selectedInvitations;
    const paginatedEmployeeInvitationsCount =
      this.props.paginatedEmployeeInvitationsCount;

    const numberOfSelectedInvites = size(
      filter(selectedInvites, (checked) => checked)
    );
    const totalNumbersAreEqual = isEqual(
      paginatedEmployeeInvitationsCount,
      numberOfSelectedInvites
    );

    if (
      (this.state.selectEveryInvitation && everyInviteActive) ||
      totalNumbersAreEqual
    ) {
      return this.setState(clearedSelection());
    } else if (this.state.selectAllInvitations[this.state.page]) {
      const selectedInvitations = transform(
        invitations,
        (acc: Record<string, boolean>, invite) => {
          if (invite.status !== "ACCEPTED") {
            acc[invite.id] = false;
          }
          return acc;
        },
        {} as Record<string, boolean>
      );
      return this.setState({
        selectedInvitations: {
          ...this.state.selectedInvitations,
          ...selectedInvitations,
        },
        selectAllInvitations: {
          ...this.state.selectAllInvitations,
          [this.state.page]: false,
        },
      });
    } else {
      return this.setState({
        selectedInvitations: {},
        selectAllInvitations: {},
      });
    }
  };

  _selectedInvitesText = () => {
    const selectedInvites = this.state.selectedInvitations;
    const numberOfSelectedInvites = size(
      filter(selectedInvites, (checked) => checked)
    );

    const paginatedEmployeeInvitationsCount =
      this.props.paginatedEmployeeInvitationsCount;
    const pageSize = numberOfSelectedInvites > this.state.limit;

    const totalNumbersAreEqual = isEqual(
      paginatedEmployeeInvitationsCount,
      numberOfSelectedInvites
    );

    // "Select all" resends by group, which cannot honor the search filter — the
    // count offered here is filtered, so the two would disagree.
    const canSelectEveryInvitation = !this.state.search;

    if (pageSize) {
      return (
        <div className="popover-box">
          <p className="selected-count">
            You have selected<p className="count">{numberOfSelectedInvites}</p>{" "}
            employee invitations.
          </p>
          {!totalNumbersAreEqual && canSelectEveryInvitation && (
            <a
              className="selectedAll-count"
              onClick={() => {
                this.setState({ selectEveryInvitation: true });
              }}
            >
              Select all{" "}
              <p className="all-count">{paginatedEmployeeInvitationsCount} </p>
              employee invitations.
            </a>
          )}
          {totalNumbersAreEqual && (
            <a className="selectedAll-count" onClick={this._clearChecked}>
              Clear Selection
            </a>
          )}
        </div>
      );
    } else {
      return (
        <div className="popover-box">
          <p className="selected-count">
            You have selected<p className="count">{numberOfSelectedInvites}</p>{" "}
            employee invitations.
          </p>
          {canSelectEveryInvitation && (
            <a
              className="selectedAll-count"
              onClick={() => {
                this.setState({ selectEveryInvitation: true });
              }}
            >
              Select all{" "}
              <p className="all-count">{paginatedEmployeeInvitationsCount} </p>
              employee invitations.
            </a>
          )}
        </div>
      );
    }
  };

  _allInvitesSelected = () => {
    const numberOfInvites = this.props.paginatedEmployeeInvitationsCount;
    const pageSize = numberOfInvites > this.state.limit;
    const nothingSelected = isEmpty(this.state.selectAllInvitations);
    const allCheckedActive = every(
      this.state.selectAllInvitations,
      (checked) => checked
    );

    const hasAllSelectedInvite = every(
      this.state.selectedInvitations,
      (selected) => selected
    );

    const selectedCheck = allCheckedActive || !hasAllSelectedInvite;

    if (!nothingSelected && selectedCheck && pageSize) {
      return (
        <div>
          {!this.state.selectEveryInvitation && hasAllSelectedInvite && (
            <>{this._selectedInvitesText()}</>
          )}
          {this.state.selectEveryInvitation && (
            <div className="popover-box">
              <p className="selected-count">
                All <p className="count">{numberOfInvites}</p> invitations in
                table are selected.
              </p>
              <a className="selectedAll-count" onClick={this._clearChecked}>
                Clear Selection
              </a>
            </div>
          )}
        </div>
      );
    }
  };

  _buildGroupTables = () => {
    const showBlankSlate =
      isEmpty(this.props.paginatedEmployeeInvitations) &&
      !this.props.isFetching;

    const invitations = this.props.paginatedEmployeeInvitations;
    const group = this.props.groupsById;

    const groupHasUniqueCodes = some(group, (group) =>
      Boolean(group.useUniqueInviteCodes)
    );

    const invitationRows = this.buildRows(groupHasUniqueCodes);

    const groupContainsUnacceptedInvite = some(
      invitations,
      (invite) => invite.status !== "ACCEPTED"
    );

    return (
      <>
        {showBlankSlate && (
          <div className="clear-search">{this._emptyState()}</div>
        )}
        {!showBlankSlate && (
          <>
            <div>{this._allInvitesSelected()}</div>
            <Table
              striped
              responsive
              style={{ marginTop: 12, marginBottom: 12 }}
            >
              <thead>
                <tr>
                  <th className="custom-checkbox">
                    {groupContainsUnacceptedInvite &&
                      !this.props.hasPayrollIntegration &&
                      this.props.hasFullyOnboarded && (
                        <Form.Check
                          type="checkbox"
                          name="selectUsers"
                          checked={
                            this.state.selectAllInvitations[this.state.page] ||
                            this.state.selectEveryInvitation
                          }
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            const checked = event.target.checked;
                            if (!checked) {
                              return this._clearChecked();
                            }
                            const selectedInvitations = transform(
                              invitations,
                              (acc: Record<string, boolean>, invite) => {
                                if (invite.status !== "ACCEPTED") {
                                  acc[invite.id] = checked;
                                }
                                return acc;
                              },
                              {} as Record<string, boolean>
                            );

                            this.setState({
                              selectedInvitations: {
                                ...this.state.selectedInvitations,
                                ...selectedInvitations,
                              },
                              selectAllInvitations: {
                                ...this.state.selectAllInvitations,
                                [this.state.page]: checked,
                              },
                            });
                          }}
                        />
                      )}
                  </th>
                  <th>Group</th>
                  <th>User Email</th>
                  <th>Date Sent</th>
                  {groupHasUniqueCodes && <th>Date of Expiration</th>}
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>{invitationRows}</tbody>
            </Table>
            <div className="paginator-box">
              <Paginator
                onChange={this._onPageChange}
                pageCount={
                  this.props.paginatedEmployeeInvitationsCount /
                  this.state.limit
                }
                page={this.state.page}
              />
            </div>
          </>
        )}
      </>
    );
  };

  _actionBtnToggle = () => {
    if (this.props.hasPayrollIntegration) return null;
    const showBlankSlate =
      isEmpty(this.props.paginatedEmployeeInvitations) &&
      !this.props.isFetching;

    if (!this.props.hasStatePlan) {
      return (
        <span style={{ paddingLeft: 5 }}>
          <Button
            icon={{
              icon: <FiUpload size={12} color={"#fffff"} />,
              position: "left",
            }}
            btnLabel="Add Employees"
            size="sm"
            name="action"
            onClick={() => this.setState({ showForm: true })}
          >
            Add Employee
          </Button>
        </span>
      );
    } else if (this.props.hasStatePlan) {
      const uploadButton = (
        <span style={{ paddingLeft: 5 }}>
          <Button
            icon={{
              icon: <FiUpload size={12} color={"#fffff"} />,
              position: "left",
            }}
            color="action"
            name="action"
            size="sm"
            onClick={() => {
              this.props.push("/dashboard/users/employees/upload");
            }}
            btnLabel="Upload File"
          />
        </span>
      );
      const singleUploadButton = (
        <span style={{ paddingLeft: 5 }}>
          <Button
            color="secondary"
            name="action"
            size="sm"
            onClick={() => {
              this.props.push("/dashboard/users/employees/upload-single");
            }}
            btnLabel="Add Single Employee"
          />
        </span>
      );
      return (
        <>
          {singleUploadButton}
          {showBlankSlate && uploadButton}
        </>
      );
    } else {
      return null;
    }
  };

  _handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const groupId = e.target.value;
    const showAllgroups = groupId === "ALL_GROUPS";
    const allGroups = showAllgroups ? undefined : groupId;

    this.setState(
      {
        selectedGroup: groupId,
        groupId: allGroups,
        ...clearedSelection(),
      },
      () => {
        void this._getPaginatedInvites();
      }
    );
  };

  _renderEmployeeTable() {
    const allUsersAccepted = every(
      this.props.paginatedEmployeeInvitations,
      (invite) => invite.status === "ACCEPTED"
    );

    const shouldShowBulkInviteButton = !allUsersAccepted;

    const numberOfInvites = this.props.paginatedEmployeeInvitationsCount;

    const inviteTable = this._buildGroupTables();

    const buttonsDisabled = every(
      this.state.selectedInvitations,
      (checked) => !checked
    );

    return (
      <>
        {this.props.error && <Alert type="error" msg={this.props.error} />}{" "}
        <div className="group-table">
          <Card className="employee_-nvitations-table">
            <div className="widget-header">
              <span>
                <Card.Title>
                  Employees:
                  <p className="invite-number">
                    Invited (<span className="number">{numberOfInvites}</span>)
                  </p>
                </Card.Title>
                <Card.Text>
                  <Row className="filters-column">
                    <Col>
                      <div className="Group-filter">
                        <Form.Select
                          className="group-filter"
                          id="inlineFormCustomSelect"
                          value={this.state.selectedGroup}
                          onChange={this._handleChange}
                        >
                          {map(this._createGroupFilter(), (group) => (
                            <option id={group.id} value={group.value}>
                              {group.name}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="search-bar">
                        <Form.Control
                          name="search"
                          as="input"
                          placeholder="Search"
                          onChange={this._debounceOnChange}
                          ref={(node: HTMLInputElement | null) => {
                            this._textInput = node;
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card.Text>
              </span>
              <div className="btn-row">
                {!this.props.hasFullyOnboarded && numberOfInvites > 0 && (
                  <Button
                    btnLabel="Launch Plan"
                    size="sm"
                    name="action"
                    onClick={() =>
                      this.props.push("/dashboard?showLaunchPlan=true")
                    }
                  />
                )}
                <span className="invite-btn">
                  {shouldShowBulkInviteButton &&
                    !this.props.hasPayrollIntegration &&
                    this.props.hasFullyOnboarded && (
                      <Button
                        icon={{
                          icon: <FiPlus size={14} color={"#fffff"} />,
                          position: "left",
                        }}
                        size="sm"
                        color="action"
                        name="submit"
                        disabled={buttonsDisabled}
                        withArrow={true}
                        loading={this.props.isResendingBulk}
                        onClick={this._handleOnClick}
                        btnLabel="Invite Selected"
                      />
                    )}
                </span>
                {this._actionBtnToggle()}
              </div>
            </div>
            {this.props.isFetching && <IconSpinner centered />}
            {inviteTable}
          </Card>
        </div>
      </>
    );
  }

  render() {
    const content = this.state.showForm ? (
      <AddEmployeeForm
        // @ts-expect-error AddEmployeeForm declares no `onClose` prop; extra prop ignored by the form (inert) — preserved from JS
        onClose={() => this.setState({ showForm: false })}
      />
    ) : (
      this._renderEmployeeTable()
    );
    return <div className="add-employee-form">{content}</div>;
  }
}

export default connector(withApollo<Props>(EmployeeInvitationsTable));
